/**
 * SoleadLab 2.0 - Alarm Panel
 *
 * Full alarm management panel with tabs for Active and History views.
 * Supports acknowledge, dismiss, filtering, and sound toggle.
 */

import { useState, useEffect } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { useMonitoringStore, useProjectStore } from '@/stores';
import * as api from '@/services/monitoringApi';
import { unlockAudio, isAudioUnlocked } from '@/services/alarmSound';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import { ADVANCED_ALARM_DATAPOINT_ID } from '@/types/monitoring';
import type { ActiveAlarmEvent, AlarmSeverity, TriggeredAdvancedRule, MatchedMetric } from '@/types/monitoring';
import './AlarmPanel.css';

interface AlarmPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'active' | 'history';

export default function AlarmPanel({ isOpen, onClose }: AlarmPanelProps) {
  // F-02E-2C: share viewers see the alarm list (active + history) but no
  // mutation controls — Acknowledge, Dismiss, Add Notes, Clear Local
  // History, and the Sound toggle are all owner surfaces. Backend rejects
  // POSTs for share visitors anyway; this gate keeps the UI honest.
  const isReadOnly = useIsReadOnlyViewer();
  const selectedSiteId = useMonitoringStore((s) => s.selectedSiteId);
  const activeAlarms = useMonitoringStore((s) => s.activeAlarms);
  const alarmHistory = useMonitoringStore((s) => s.alarmHistory);
  const alarmSoundEnabled = useMonitoringStore((s) => s.alarmSoundEnabled);
  const acknowledgeAlarm = useMonitoringStore((s) => s.acknowledgeAlarm);
  const dismissAlarm = useMonitoringStore((s) => s.dismissAlarm);
  const clearAlarmHistory = useMonitoringStore((s) => s.clearAlarmHistory);
  const setAlarmSoundEnabled = useMonitoringStore((s) => s.setAlarmSoundEnabled);
  const loadAlarms = useMonitoringStore((s) => s.loadAlarms);
  const rawTriggeredAdvanced = useMonitoringStore((s) => s.triggeredAdvancedRules);
  const triggeredAdvancedRules = Array.isArray(rawTriggeredAdvanced) ? rawTriggeredAdvanced : [];
  // Live-mode display overlay: matched offenders per advanced rule_id, recomputed
  // from current runtime data. Display-only — never an alarm source.
  const advancedMatchedByRule = useMonitoringStore((s) => s.advancedMatchedByRule);
  // Exclude-from-alarm (Slice 2): append an offender to a rule's exclusions + save.
  const addRuleExclusion = useMonitoringStore((s) => s.addRuleExclusion);
  // Advanced rules also live in the project blob (base_data.monitoringConfig), which is
  // what the frontend reloads on refresh. addRuleExclusion/saveConfig only writes
  // monitoring_configs (D1, for the DO); marking the project modified lets the project's
  // own save path (useAutoSave + beforeunload) persist the exclusion to the blob so it
  // survives a hard refresh.
  const markProjectModified = useProjectStore((s) => s.markModified);

  const [activeTab, setActiveTab] = useState<TabId>('active');
  const [severityFilter, setSeverityFilter] = useState<AlarmSeverity | 'all'>('all');
  const [historyAlarms, setHistoryAlarms] = useState<ActiveAlarmEvent[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [ackNotes, setAckNotes] = useState<Record<string, string>>({});
  // Which offender is mid-exclude (`${ruleId}|${entity}|${metric}`) — disables its
  // button + guards against duplicate concurrent writes; and a small save-failure note.
  const [excludingKey, setExcludingKey] = useState<string | null>(null);
  const [excludeError, setExcludeError] = useState<string | null>(null);

  // Exclude an offending entity/metric from an advanced rule. Editor-only (hidden for
  // read-only/share viewers at the call site) and requires a site to persist to.
  const handleExclude = async (ruleId: string, entityId: string, metric: string) => {
    if (excludingKey) return; // one exclude in flight at a time
    const key = `${ruleId}|${entityId}|${metric}`;
    setExcludingKey(key);
    setExcludeError(null);
    try {
      const ok = await addRuleExclusion(ruleId, { entity_id: entityId, metric });
      if (ok) {
        // Persist the exclusion into the project blob too (survives a hard refresh):
        // mark the project modified so useAutoSave / beforeunload serialize the updated
        // monitoring config. saveConfig already handled the D1 (DO) side.
        markProjectModified();
      } else {
        setExcludeError('Could not save the exclusion — changes are unsaved. Use Publish to retry.');
      }
    } catch {
      setExcludeError('Could not save the exclusion — changes are unsaved. Use Publish to retry.');
    } finally {
      setExcludingKey(null);
    }
  };
  // Editors can exclude only when not a share/read-only viewer and a site exists to
  // persist to. Live sentinel alarms always have a site; upload/sample without one hides it.
  const canExcludeOffenders = !isReadOnly && !!selectedSiteId;

  const PAGE_SIZE = 20;

  // Refresh alarms when panel opens
  useEffect(() => {
    if (isOpen && selectedSiteId) {
      loadAlarms(selectedSiteId);
    }
  }, [isOpen, selectedSiteId]);

  // Load alarm history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history' && selectedSiteId) {
      fetchHistory();
    }
  }, [activeTab, selectedSiteId, historyPage, severityFilter]);

  const fetchHistory = async () => {
    if (!selectedSiteId) return;
    setIsLoadingHistory(true);
    try {
      const result = await api.getAlarmHistory(selectedSiteId, {
        limit: PAGE_SIZE,
        page: historyPage + 1, // UI page is 0-based; the handler is 1-based
        severity: severityFilter !== 'all' ? severityFilter : undefined,
      });
      setHistoryAlarms(result.items ?? []);
      setHistoryTotal(result.total ?? 0);
    } catch (err) {
      console.error('Failed to load alarm history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleAcknowledge = async (alarmId: string) => {
    await acknowledgeAlarm(alarmId, ackNotes[alarmId]);
    setAckNotes((prev) => {
      const next = { ...prev };
      delete next[alarmId];
      return next;
    });
  };

  const handleSoundToggle = () => {
    const newValue = !alarmSoundEnabled;
    setAlarmSoundEnabled(newValue);
    if (newValue && !isAudioUnlocked()) {
      unlockAudio();
    }
  };

  // Filter active alarms by severity. Live-mode advanced-rule alarms arrive via
  // activeAlarms (synthetic advanced datapoint_id); upload-mode ones arrive via
  // triggeredAdvancedRules. Drop any activeAlarms row already represented in
  // triggeredAdvancedRules so a rule shown in both channels isn't doubled.
  const rawSafeAlarms = Array.isArray(activeAlarms) ? activeAlarms : [];
  const advancedRuleIds = new Set(triggeredAdvancedRules.map((r) => r.rule_id));
  // The Active tab shows only OPEN alarms (state 'active' or 'acknowledged').
  // Resolved/dismissed alarms never appear here — they belong to the History tab.
  const safeAlarms = rawSafeAlarms.filter(
    (a) => (a.state === 'active' || a.state === 'acknowledged')
      && !(a.datapoint_id === ADVANCED_ALARM_DATAPOINT_ID && a.rule_id && advancedRuleIds.has(a.rule_id))
  );
  const filteredActive = severityFilter === 'all'
    ? safeAlarms
    : safeAlarms.filter((a) => a.severity === severityFilter);

  const advCritical = triggeredAdvancedRules.filter((r) => r.severity === 'critical').length;
  const advWarning = triggeredAdvancedRules.filter((r) => r.severity === 'warning').length;
  const criticalCount = safeAlarms.filter((a) => a.severity === 'critical').length + advCritical;
  const warningCount = safeAlarms.filter((a) => a.severity === 'warning').length + advWarning;

  const totalHistoryPages = Math.ceil(historyTotal / PAGE_SIZE);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Alarm Management"
      size="lg"
    >
      <div className="ap-content">
        {/* Header Stats */}
        <div className="ap-stats">
          <div className="ap-stat">
            <span className="ap-stat__value ap-stat__value--total">{safeAlarms.length}</span>
            <span className="ap-stat__label">Active</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat__value ap-stat__value--critical">{criticalCount}</span>
            <span className="ap-stat__label">Critical</span>
          </div>
          <div className="ap-stat">
            <span className="ap-stat__value ap-stat__value--warning">{warningCount}</span>
            <span className="ap-stat__label">Warning</span>
          </div>
          {!isReadOnly && (
            <div className="ap-stat ap-stat--toggle" data-testid="alarm-panel-sound-toggle">
              <label className="ap-sound-toggle">
                <input
                  type="checkbox"
                  checked={alarmSoundEnabled}
                  onChange={handleSoundToggle}
                />
                <span className="ap-sound-toggle__label">
                  {alarmSoundEnabled ? 'Sound On' : 'Sound Off'}
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="ap-tabs">
          <button
            className={`ap-tab ${activeTab === 'active' ? 'ap-tab--active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Active ({safeAlarms.length})
          </button>
          <button
            className={`ap-tab ${activeTab === 'history' ? 'ap-tab--active' : ''}`}
            onClick={() => { setActiveTab('history'); setHistoryPage(0); }}
          >
            History
          </button>
        </div>

        {/* Severity Filter */}
        <div className="ap-filter">
          <span className="ap-filter__label">Filter:</span>
          {(['all', 'critical', 'warning'] as const).map((sev) => (
            <button
              key={sev}
              className={`ap-filter__btn ${severityFilter === sev ? 'ap-filter__btn--active' : ''} ${sev !== 'all' ? `ap-filter__btn--${sev}` : ''}`}
              onClick={() => { setSeverityFilter(sev); setHistoryPage(0); }}
            >
              {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
            </button>
          ))}
        </div>

        {/* Active Alarms Tab */}
        {activeTab === 'active' && (
          <div className="ap-alarm-list">
            {excludeError && (
              <div className="ap-exclude-error" role="alert" style={{ fontSize: 12, color: 'var(--sl-danger, #c0392b)', padding: '4px 2px' }}>
                {excludeError}
              </div>
            )}
            {filteredActive.map((alarm) => (
              <AlarmCard
                key={alarm.id}
                alarm={alarm}
                matchedMetrics={alarm.rule_id ? advancedMatchedByRule?.[alarm.rule_id] : undefined}
                ruleId={alarm.rule_id}
                onExclude={canExcludeOffenders && alarm.rule_id ? (e, m) => handleExclude(alarm.rule_id!, e, m) : undefined}
                excludingKey={excludingKey}
                notes={ackNotes[alarm.id] || ''}
                onNotesChange={(val) => setAckNotes((p) => ({ ...p, [alarm.id]: val }))}
                onAcknowledge={() => handleAcknowledge(alarm.id)}
                onDismiss={() => dismissAlarm(alarm.id)}
                readonly={isReadOnly}
              />
            ))}

            {/* Triggered Advanced Rules (upload/sample mode) */}
            {triggeredAdvancedRules
              .filter((r) => severityFilter === 'all' || r.severity === severityFilter)
              .map((rule) => (
                <AdvancedAlarmCard
                  key={rule.rule_id}
                  rule={rule}
                  onExclude={canExcludeOffenders ? (e, m) => handleExclude(rule.rule_id, e, m) : undefined}
                  excludingKey={excludingKey}
                />
              ))
            }

            {filteredActive.length === 0 && triggeredAdvancedRules.length === 0 && (
              <div className="ap-empty">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>No active alarms</span>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="ap-alarm-list">
            {isLoadingHistory ? (
              <div className="ap-loading">Loading alarm history...</div>
            ) : historyAlarms.length > 0 ? (
              <>
                {historyAlarms.map((alarm) => (
                  <AlarmCard
                    key={alarm.id}
                    alarm={alarm}
                    readonly
                  />
                ))}
                {/* Pagination */}
                {totalHistoryPages > 1 && (
                  <div className="ap-pagination">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                      disabled={historyPage === 0}
                    >
                      Previous
                    </Button>
                    <span className="ap-pagination__text">
                      Page {historyPage + 1} of {totalHistoryPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages - 1, p + 1))}
                      disabled={historyPage >= totalHistoryPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : alarmHistory.length > 0 ? (
              // Fall back to locally dismissed alarms
              <>
                {alarmHistory
                  .filter((a) => severityFilter === 'all' || a.severity === severityFilter)
                  .map((alarm) => (
                    <AlarmCard key={alarm.id} alarm={alarm} readonly />
                  ))}
                {!isReadOnly && (
                  <div className="ap-history-actions">
                    <Button variant="ghost" size="sm" onClick={clearAlarmHistory}>
                      Clear Local History
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="ap-empty">
                <span>No alarm history</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================
// OffenderList Sub-Component
// ============================================
// Shared offender/matched-metric list for advanced alarms (live overlay + upload
// rich card). Each row is `entity.metric = value`. When `onExclude` + `ruleId` are
// provided (authorized editor, real rule), each row also offers an "Exclude" action
// that adds the pair to the rule's exclusions via monitoringStore.addRuleExclusion.
// Exclude modifies rule CONFIG only — it is NOT a dismiss/resolve/acknowledge.

function OffenderList({
  metrics,
  ruleId,
  onExclude,
  excludingKey,
}: {
  metrics: MatchedMetric[];
  ruleId?: string;
  onExclude?: (entityId: string, metric: string) => void;
  excludingKey?: string | null;
}) {
  const canExclude = !!ruleId && !!onExclude;
  return (
    <div className="ap-card__details" style={{ marginTop: 4 }}>
      {metrics.slice(0, 5).map((m, i) => {
        const pending = excludingKey === `${ruleId}|${m.entity_id}|${m.metric}`;
        return (
          <span key={i} className="ap-card__detail" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>{m.entity_id}.{m.metric} = {m.value}</span>
            {canExclude && (
              <button
                type="button"
                onClick={() => onExclude!(m.entity_id, m.metric)}
                disabled={pending}
                title="Exclude this string/metric from the rule (adds it to the rule's exclusions and saves the config). This is not the same as dismissing the alarm."
                style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                  border: '1px solid var(--sl-border, #d0d0d0)', background: 'transparent',
                  color: 'var(--text-secondary)', cursor: pending ? 'default' : 'pointer',
                  opacity: pending ? 0.6 : 1, lineHeight: 1.4,
                }}
              >
                {pending ? 'Excluding…' : 'Exclude'}
              </button>
            )}
          </span>
        );
      })}
      {metrics.length > 5 && (
        <span className="ap-card__detail" style={{ color: 'var(--text-muted)' }}>
          +{metrics.length - 5} more
        </span>
      )}
    </div>
  );
}

// ============================================
// AlarmCard Sub-Component
// ============================================

function AlarmCard({
  alarm,
  matchedMetrics,
  ruleId,
  onExclude,
  excludingKey,
  notes,
  onNotesChange,
  onAcknowledge,
  onDismiss,
  readonly = false,
}: {
  alarm: ActiveAlarmEvent;
  matchedMetrics?: MatchedMetric[];
  ruleId?: string;
  onExclude?: (entityId: string, metric: string) => void;
  excludingKey?: string | null;
  notes?: string;
  onNotesChange?: (val: string) => void;
  onAcknowledge?: () => void;
  onDismiss?: () => void;
  readonly?: boolean;
}) {
  const [showNotes, setShowNotes] = useState(false);

  const isTriggered = alarm.state === 'active';
  const isAcknowledged = alarm.state === 'acknowledged';
  // Site-level advanced-rule alarms carry a synthetic datapoint_id; render them
  // as "Advanced rule" rather than exposing the internal sentinel.
  const isAdvanced = alarm.datapoint_id === ADVANCED_ALARM_DATAPOINT_ID;

  return (
    <div className={`ap-card ap-card--${alarm.severity} ${isAcknowledged ? 'ap-card--acked' : ''}`}>
      <div className="ap-card__header">
        <span className={`ap-card__severity ap-card__severity--${alarm.severity}`}>
          {alarm.severity.toUpperCase()}
        </span>
        {isAdvanced && (
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            ADVANCED
          </span>
        )}
        <span className={`ap-card__state ap-card__state--${alarm.state}`}>
          {alarm.state}
        </span>
        <span className="ap-card__time">
          {new Date(alarm.triggered_at).toLocaleString()}
        </span>
      </div>

      <div className="ap-card__body">
        <div className="ap-card__message">
          {alarm.message || `${alarm.metric} ${alarm.operator} ${alarm.threshold}`}
        </div>
        <div className="ap-card__details">
          <span className="ap-card__detail">
            <span className="ap-card__detail-label">{isAdvanced ? 'Rule:' : 'Point:'}</span>{' '}
            {isAdvanced ? 'Advanced rule' : alarm.datapoint_id}
          </span>
          <span className="ap-card__detail">
            <span className="ap-card__detail-label">Metric:</span> {alarm.metric}
          </span>
          <span className="ap-card__detail">
            <span className="ap-card__detail-label">Value:</span>{' '}
            {alarm.actual_value !== null ? alarm.actual_value : 'N/A'}
            {' / '}
            <span className="ap-card__detail-label">Threshold:</span> {alarm.threshold}
          </span>
        </div>

        {/* Advanced-rule offender list (live mode). Display-only overlay recovered
            from the frontend evaluator; falls back to the message string above when
            no matched metrics are available. Rendered here, never as its own card.
            Authorized editors get a per-row Exclude action (rule config edit). */}
        {isAdvanced && matchedMetrics && matchedMetrics.length > 0 && (
          <OffenderList
            metrics={matchedMetrics}
            ruleId={ruleId}
            onExclude={onExclude}
            excludingKey={excludingKey}
          />
        )}

        {alarm.acknowledged_at && (
          <div className="ap-card__ack-info">
            Acknowledged at {new Date(alarm.acknowledged_at).toLocaleString()}
            {alarm.acknowledged_by && ` by ${alarm.acknowledged_by}`}
            {alarm.notes && <div className="ap-card__ack-notes">Notes: {alarm.notes}</div>}
          </div>
        )}

        {alarm.resolved_at && (
          <div className="ap-card__resolved-info">
            Resolved at {new Date(alarm.resolved_at).toLocaleString()}
          </div>
        )}
      </div>

      {!readonly && (
        <div className="ap-card__actions">
          {isTriggered && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setShowNotes(!showNotes)}>
                {showNotes ? 'Hide Notes' : 'Add Notes'}
              </Button>
              <Button variant="primary" size="sm" onClick={onAcknowledge}>
                Acknowledge
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      )}

      {showNotes && !readonly && (
        <div className="ap-card__notes">
          <textarea
            className="ap-card__notes-input"
            placeholder="Add notes for acknowledgement..."
            value={notes || ''}
            onChange={(e) => onNotesChange?.(e.target.value)}
            rows={2}
          />
        </div>
      )}
    </div>
  );
}

// ============================================
// AdvancedAlarmCard Sub-Component
// ============================================

const TYPE_BADGE: Record<string, string> = {
  zero_check: 'ZERO CHECK',
  deviation: 'DEVIATION',
  expression: 'EXPRESSION',
};

function AdvancedAlarmCard({
  rule,
  onExclude,
  excludingKey,
}: {
  rule: TriggeredAdvancedRule;
  onExclude?: (entityId: string, metric: string) => void;
  excludingKey?: string | null;
}) {
  return (
    <div className={`ap-card ap-card--${rule.severity}`}>
      <div className="ap-card__header">
        <span className={`ap-card__severity ap-card__severity--${rule.severity}`}>
          {rule.severity.toUpperCase()}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {TYPE_BADGE[rule.rule_type] || rule.rule_type}
        </span>
      </div>

      <div className="ap-card__body">
        <div className="ap-card__message" style={{ fontWeight: 600 }}>
          {rule.rule_name}
        </div>
        {rule.message && (
          <div className="ap-card__message" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {rule.message}
          </div>
        )}
        <div className="ap-card__details">
          <span className="ap-card__detail">{rule.details}</span>
        </div>
        {rule.matched_metrics && rule.matched_metrics.length > 0 && (
          <OffenderList
            metrics={rule.matched_metrics}
            ruleId={rule.rule_id}
            onExclude={onExclude}
            excludingKey={excludingKey}
          />
        )}
      </div>
    </div>
  );
}
